import { useMemo, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import medicareCard from '@/assets/images/medicare-card-mockup.svg';
import { RecaptchaNotice } from '@/components/RecaptchaNotice';
import { RCta } from '../RCta';
import { RIcon } from '../RIcon';
import { WCta } from '../WCta';
import { WregPhase } from '../WregPhase';
import { WREG_WORKING_MS } from '../wregSteps';
import {
  EMPTY_INTAKE,
  MBI_MASK,
  dobIsOutOfRange,
  emailIsValid,
  formatDob,
  formatMbi,
  formatPhone,
  intakeIsComplete,
  type WregIntakeForm,
  type WregIntakeTextField,
} from './wregIntakeFields';

type Props = {
  notice?: string | null;
  initial?: WregIntakeForm;
  onSubmit: (form: WregIntakeForm) => void | Promise<void>;
  onSubmitted?: () => void;
};

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path
      opacity=".35"
      d="M18,21H6c-1.657,0-3-1.343-3-3V7h18v11C21,19.657,19.657,21,18,21z"
    />
    <path d="M18,3H6C4.343,3,3,4.343,3,6v1h18V6C21,4.343,19.657,3,18,3z" />
    <circle cx="14" cy="18" r="1" />
    <circle cx="10" cy="18" r="1" />
    <circle cx="6" cy="18" r="1" />
    <circle cx="6" cy="14" r="1" />
    <circle cx="10" cy="14" r="1" />
    <circle cx="14" cy="14" r="1" />
    <circle cx="18" cy="14" r="1" />
    <circle cx="18" cy="10" r="1" />
    <circle cx="14" cy="10" r="1" />
    <circle cx="10" cy="10" r="1" />
  </svg>
);

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path
      opacity=".35"
      d="M16.48,3.086c-1.653,0.222-5.452,1.091-8.878,4.517s-4.295,7.224-4.517,8.878c-0.259,1.198,0.07,2.497,1.001,3.428c1.456,1.456,6.73-3.818,5.274-5.274l-0.929-0.929l5.274-5.274l0.929,0.929c1.456,1.456,6.73-3.818,5.274-5.274C18.977,3.155,17.678,2.826,16.48,3.086z"
    />
    <path d="M19.911,4.089l-5.273,5.273c1.457,1.453,3.815,1.453,5.271-0.003C21.363,7.905,21.364,5.546,19.911,4.089z" />
    <path d="M9.363,14.637L4.09,19.91c1.456,1.453,3.815,1.453,5.271-0.003C10.815,18.452,10.816,16.094,9.363,14.637z" />
  </svg>
);

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path
      d="M2,17V7c0-1.657,1.343-3,3-3h14c1.657,0,3,1.343,3,3v10c0,1.657-1.343,3-3,3H5C3.343,20,2,18.657,2,17z"
      opacity=".35"
    />
    <path d="M22,7c0-0.478-0.122-0.924-0.321-1.325l-8.187,4.912c-0.919,0.552-2.063,0.552-2.983,0L2.321,5.675C2.122,6.076,2,6.522,2,7v1.981l6.964,4.179c0.937,0.562,1.986,0.843,3.036,0.843c1.049,0,2.1-0.281,3.035-0.843L22,8.981V7z" />
  </svg>
);

const GENDERS = [
  ['Female', 'wreg.intake.gender.f'],
  ['Male', 'wreg.intake.gender.m'],
] as const;

type IntakeErrorKey = keyof WregIntakeForm;
type IntakeErrors = Partial<Record<IntakeErrorKey, string>>;

const INTAKE_FIELD_ORDER: IntakeErrorKey[] = [
  'first',
  'last',
  'dob',
  'gender',
  'mbi',
  'email',
  'phone',
  'phone2',
  'tcAccepted',
];

const REQUIRED_FIELD_MESSAGE = 'This field is required.';
const INVALID_DOB_MESSAGE = 'Enter a complete date of birth.';
const INVALID_MBI_MESSAGE = 'Enter all 11 characters from your Medicare card.';
const INVALID_EMAIL_MESSAGE = 'Enter a valid email address.';
const INVALID_PHONE_MESSAGE = 'Enter a 10-digit phone number.';
const INVALID_PHONE2_MESSAGE = 'Enter your phone number again.';
const CONSENT_MESSAGE = 'Please agree to the Terms of Service and Privacy Policy.';

const errorId = (field: IntakeErrorKey) => `wreg-int-${field}-err`;

const WregIntake = ({ notice, initial, onSubmit, onSubmitted }: Props) => {
  const { t } = useTranslation();
  const [f, setF] = useState<WregIntakeForm>(initial ?? EMPTY_INTAKE);
  const [mbiHelp, setMbiHelp] = useState(false);
  const [touched, setTouched] = useState<Set<IntakeErrorKey>>(() => new Set());
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const set = (k: WregIntakeTextField) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF((p) => ({ ...p, [k]: e.target.value }));
  const touch = (field: IntakeErrorKey) => () =>
    setTouched((previous) => new Set(previous).add(field));

  const phoneDisplay = formatPhone(f.phone);
  const phone2Display = formatPhone(f.phone2);
  const phone2Mismatch =
    f.phone.length > 0 &&
    f.phone2.length > 0 &&
    !f.phone.startsWith(f.phone2) &&
    !f.phone2.startsWith(f.phone);
  const dobOutOfRange = dobIsOutOfRange(f.dob);
  const valid = intakeIsComplete(f);
  const errors = useMemo<IntakeErrors>(() => {
    const next: IntakeErrors = {};
    if (!f.first.trim()) next.first = REQUIRED_FIELD_MESSAGE;
    if (!f.last.trim()) next.last = REQUIRED_FIELD_MESSAGE;
    if (dobOutOfRange) next.dob = t('wreg.intake.dobnotice');
    else if (f.dob.replace(/\D/g, '').length !== 8) next.dob = INVALID_DOB_MESSAGE;
    if (!f.gender) next.gender = REQUIRED_FIELD_MESSAGE;
    if (f.mbi.replace(/[^A-Za-z0-9]/g, '').length !== 11) next.mbi = INVALID_MBI_MESSAGE;
    if (!f.email.trim()) next.email = REQUIRED_FIELD_MESSAGE;
    else if (!emailIsValid(f.email)) {
      next.email = INVALID_EMAIL_MESSAGE;
    }
    if (f.phone.length !== 10) next.phone = INVALID_PHONE_MESSAGE;
    if (phone2Mismatch) next.phone2 = t('wreg.intake.phone2.err');
    else if (f.phone2.length !== 10) next.phone2 = INVALID_PHONE2_MESSAGE;
    if (!f.tcAccepted) next.tcAccepted = CONSENT_MESSAGE;
    return next;
  }, [dobOutOfRange, f, phone2Mismatch, t]);
  const showError = (field: IntakeErrorKey) =>
    Boolean(errors[field]) &&
    (submitAttempted ||
      touched.has(field) ||
      (field === 'dob' && dobOutOfRange) ||
      (field === 'phone2' && phone2Mismatch));

  const validateBeforeSubmit = () => {
    if (valid) return true;

    setSubmitAttempted(true);
    const firstInvalid = INTAKE_FIELD_ORDER.find((field) => errors[field]);
    if (firstInvalid) {
      requestAnimationFrame(() => {
        const id = firstInvalid === 'tcAccepted' ? 'wreg-int-terms' : `wreg-int-${firstInvalid}`;
        const control = document.getElementById(id);
        control?.focus();
        control?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      });
    }
    return false;
  };

  return (
    <div className="rg-screen">
      <div className="rg-body-wrap">
        <div className="rg-body">
          <div className="rg-inner">
            <div className="rg-head-block">
              <WregPhase n={1} />
              <h1 className="rg-h1">{t('wreg.intake.title')}</h1>
              <p className="rg-sub">{t('wreg.intake.sub')}</p>
            </div>
            {notice && (
              <div className="rg-disclosure" style={{ background: 'var(--hh-gold-4, #F6E3C9)' }}>
                <span className="ic" aria-hidden="true">i</span>
                <p>{notice}</p>
              </div>
            )}
            <div className="rg-field-row">
              <div className="rg-field">
                <label className="rg-label" htmlFor="wreg-int-first">
                  {t('wreg.intake.first')}
                </label>
                <div className="rg-input-wrap">
                  <input
                    id="wreg-int-first"
                    className="rg-input"
                    autoComplete="given-name"
                    value={f.first}
                    onChange={set('first')}
                    onBlur={touch('first')}
                    aria-invalid={showError('first') || undefined}
                    aria-describedby={showError('first') ? errorId('first') : undefined}
                  />
                </div>
                {showError('first') && <span className="rg-error-hint" id={errorId('first')}>{errors.first}</span>}
              </div>
              <div className="rg-field">
                <label className="rg-label" htmlFor="wreg-int-last">
                  {t('wreg.intake.last')}
                </label>
                <div className="rg-input-wrap">
                  <input
                    id="wreg-int-last"
                    className="rg-input"
                    autoComplete="family-name"
                    value={f.last}
                    onChange={set('last')}
                    onBlur={touch('last')}
                    aria-invalid={showError('last') || undefined}
                    aria-describedby={showError('last') ? errorId('last') : undefined}
                  />
                </div>
                {showError('last') && <span className="rg-error-hint" id={errorId('last')}>{errors.last}</span>}
              </div>
            </div>
            <div className="rg-field">
              <label className="rg-label" htmlFor="wreg-int-dob">
                {t('wreg.intake.dob.label')}
              </label>
              <div className="rg-input-wrap wreg-mask-wrap">
                <span className="rg-input-lead">
                  <CalendarIcon />
                </span>
                <input
                  id="wreg-int-dob"
                  className="rg-input has-lead"
                  autoComplete="off"
                  inputMode="numeric"
                  value={f.dob}
                  onChange={(e) => setF((p) => ({ ...p, dob: formatDob(e.target.value) }))}
                  onBlur={touch('dob')}
                  placeholder=""
                  aria-invalid={showError('dob') || undefined}
                  aria-describedby={showError('dob') ? errorId('dob') : undefined}
                />
                <span className="wreg-mask-ghost" aria-hidden="true">
                  <i>{f.dob}</i>
                  {t('wreg.intake.dob.ph').slice(f.dob.length)}
                </span>
              </div>
              {showError('dob') && <span className="rg-error-hint" id={errorId('dob')}>{errors.dob}</span>}
            </div>
            <div className="rg-field">
              <label className="rg-label" htmlFor="wreg-int-gender">
                {t('wreg.intake.gender.label')}
              </label>
              <div className="rg-input-wrap">
                <select
                  id="wreg-int-gender"
                  className={`rg-input rg-select${f.gender ? '' : ' placeholder'}`}
                  value={f.gender}
                  onChange={set('gender')}
                  onBlur={touch('gender')}
                  aria-invalid={showError('gender') || undefined}
                  aria-describedby={showError('gender') ? errorId('gender') : undefined}
                >
                  <option value="" disabled hidden>
                    {t('wreg.intake.gender.ph')}
                  </option>
                  {GENDERS.map(([g, key]) => (
                    <option key={g} value={g}>
                      {t(key)}
                    </option>
                  ))}
                </select>
                <span className="rg-select-chev" aria-hidden="true">
                  <RIcon.chevR />
                </span>
              </div>
              {showError('gender') && <span className="rg-error-hint" id={errorId('gender')}>{errors.gender}</span>}
            </div>
            <div className="rg-field">
              <label className="rg-label" htmlFor="wreg-int-mbi">
                {t('wreg.intake.mbi.label')}
              </label>
              <div className="rg-input-wrap wreg-mbi-wrap">
                <input
                  id="wreg-int-mbi"
                  className="rg-input mbi"
                  autoComplete="off"
                  value={f.mbi}
                  onChange={(e) => setF((p) => ({ ...p, mbi: formatMbi(e.target.value) }))}
                  onBlur={touch('mbi')}
                  placeholder=""
                  style={{ width: '18.5ch', maxWidth: '100%' }}
                  aria-invalid={showError('mbi') || undefined}
                  aria-describedby={showError('mbi') ? errorId('mbi') : undefined}
                />
                <span className="wreg-mbi-ghost" aria-hidden="true">
                  <i>{f.mbi}</i>
                  {MBI_MASK.slice(f.mbi.length)}
                </span>
              </div>
              <span className="rg-hint">
                {t('wreg.intake.mbi.hint')}
                <button type="button" className="c-link" onClick={() => setMbiHelp(true)}>
                  {t('wreg.intake.mbi.how')}
                </button>
              </span>
              {showError('mbi') && <span className="rg-error-hint" id={errorId('mbi')}>{errors.mbi}</span>}
            </div>
            <div className="rg-field">
              <label className="rg-label" htmlFor="wreg-int-email">
                {t('wreg.intake.email.label')}
              </label>
              <div className="rg-input-wrap">
                <span className="rg-input-lead">
                  <MailIcon />
                </span>
                <input
                  id="wreg-int-email"
                  className="rg-input has-lead"
                  autoComplete="email"
                  type="email"
                  value={f.email}
                  onChange={set('email')}
                  onBlur={touch('email')}
                  placeholder={t('wreg.intake.email.ph')}
                  aria-invalid={showError('email') || undefined}
                  aria-describedby={showError('email') ? errorId('email') : undefined}
                />
              </div>
              <span className="rg-hint">{t('wreg.intake.email.hint')}</span>
              {showError('email') && <span className="rg-error-hint" id={errorId('email')}>{errors.email}</span>}
            </div>
            <div className="rg-field">
              <label className="rg-label" htmlFor="wreg-int-phone">
                {t('wreg.intake.phone.label')}
              </label>
              <div className="rg-input-wrap wreg-mask-wrap">
                <span className="rg-input-lead">
                  <PhoneIcon />
                </span>
                <input
                  id="wreg-int-phone"
                  className="rg-input has-lead"
                  autoComplete="tel-national"
                  type="tel"
                  inputMode="numeric"
                  value={phoneDisplay}
                  onChange={(e) =>
                    setF((p) => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))
                  }
                  onBlur={touch('phone')}
                  placeholder=""
                  aria-invalid={showError('phone') || undefined}
                  aria-describedby={showError('phone') ? errorId('phone') : undefined}
                />
                <span className="wreg-mask-ghost" aria-hidden="true">
                  <i>{phoneDisplay}</i>
                  {t('wreg.intake.phone.ph').slice(phoneDisplay.length)}
                </span>
              </div>
              <span className="rg-hint">{t('wreg.intake.phone.hint')}</span>
              {showError('phone') && <span className="rg-error-hint" id={errorId('phone')}>{errors.phone}</span>}
            </div>
            <div className="rg-field">
              <label className="rg-label" htmlFor="wreg-int-phone2">
                {t('wreg.intake.phone2.label')}
              </label>
              <div className="rg-input-wrap wreg-mask-wrap">
                <span className="rg-input-lead">
                  <PhoneIcon />
                </span>
                <input
                  id="wreg-int-phone2"
                  className="rg-input has-lead"
                  autoComplete="off"
                  type="tel"
                  inputMode="numeric"
                  value={phone2Display}
                  onChange={(e) =>
                    setF((p) => ({ ...p, phone2: e.target.value.replace(/\D/g, '').slice(0, 10) }))
                  }
                  onBlur={touch('phone2')}
                  placeholder=""
                  aria-invalid={showError('phone2') || undefined}
                  aria-describedby={showError('phone2') ? errorId('phone2') : undefined}
                />
                <span className="wreg-mask-ghost" aria-hidden="true">
                  <i>{phone2Display}</i>
                  {t('wreg.intake.phone.ph').slice(phone2Display.length)}
                </span>
              </div>
              {showError('phone2') && <span className="rg-error-hint" id={errorId('phone2')}>{errors.phone2}</span>}
            </div>
            <label className={`c-checkopt wreg-consent${f.tcAccepted ? ' on' : ''}${showError('tcAccepted') ? ' is-invalid' : ''}`}>
              <span className="wreg-consent-check">
                <input
                  type="checkbox"
                  id="wreg-int-terms"
                  className="wreg-consent-input"
                  checked={f.tcAccepted}
                  onChange={() => {
                    touch('tcAccepted')();
                    setF((p) => ({ ...p, tcAccepted: !p.tcAccepted }));
                  }}
                  aria-invalid={showError('tcAccepted') || undefined}
                  aria-describedby={showError('tcAccepted') ? errorId('tcAccepted') : undefined}
                />
                <span className="c-checkopt-box" aria-hidden="true">
                  <RIcon.check />
                </span>
              </span>
              <span className="c-checkopt-body">
                <span className="c-checkopt-title">
                  {t('wreg.intake.legal.pre')}
                  <a
                    className="c-link"
                    href={t('general.links.tos')}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t('wreg.intake.legal.terms')}
                  </a>
                  {t('wreg.intake.legal.mid')}
                  <a
                    className="c-link"
                    href={t('general.links.privacy-policy')}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t('wreg.intake.legal.privacy')}
                  </a>
                  {t('wreg.intake.legal.post')}
                </span>
              </span>
            </label>
            {showError('tcAccepted') && <span className="rg-error-hint" id={errorId('tcAccepted')}>{errors.tcAccepted}</span>}
          </div>
        </div>
      </div>
      <div className="rg-foot">
        <div className="wreg-foot-row">
          <WCta
            beforeClick={validateBeforeSubmit}
            dimmed={!valid}
            working={t('wreg.intake.cta_loading')}
            minWorkingMs={WREG_WORKING_MS}
            onClick={() => onSubmit(f)}
            onDone={onSubmitted}
          >
            {t('wreg.intake.cta')}
          </WCta>
          {!valid && <span className="wreg-foot-tip">{t('wreg.foot.fillall')}</span>}
        </div>
        <div className="wreg-foot-legal">
          <RecaptchaNotice />
        </div>
      </div>

      {mbiHelp && (
        <div className="rg-modal-back show" onClick={() => setMbiHelp(false)}>
          <div className="rg-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="dsb dsb-secondary dsb-s wreg-modal-close"
              onClick={() => setMbiHelp(false)}
            >
              {t('wreg.mbi.close')}
            </button>
            <div className="rg-h1" style={{ paddingRight: 96 }}>
              {t('wreg.mbi.title')}
            </div>
            <p className="rg-sub">{t('wreg.mbi.sub')}</p>
            <img
              className="rg-mbi-img"
              src={medicareCard}
              width="400"
              height="260"
              alt={t('wreg.mbi.alt')}
            />
            <RCta onClick={() => setMbiHelp(false)}>{t('wreg.mbi.cta')}</RCta>
          </div>
        </div>
      )}
    </div>
  );
};

export { WregIntake };
